import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { HeroBanner } from '../components/HeroBanner';
import { DateNav } from '../components/DateNav';
import { ScheduleTable } from '../components/ScheduleTable';
import { Footer } from '../components/Footer';
import { TopNav } from '../components/TopNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Poker Tour page — the daily tournament schedule (previously the home page).
 * Now lives at /poker-tour. Reuses the full hero + dates + table experience.
 */
const PokerTourPage = () => {
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [lockedDate, setLockedDate] = useState(() => {
    try { return localStorage.getItem('rdpt_locked_date') || null; } catch { return null; }
  });

  const handleLockToggle = (date) => {
    setLockedDate((prev) => {
      const next = prev === date ? null : date;
      try {
        if (next) localStorage.setItem('rdpt_locked_date', next);
        else localStorage.removeItem('rdpt_locked_date');
      } catch (e) { console.warn('localStorage unavailable:', e.message); }
      if (next) setSelectedDate(next);
      return next;
    });
  };

  const fetchDates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tournaments/dates`);
      setDates(response.data);
      if (response.data.length > 0 && !selectedDate) {
        const params = new URLSearchParams(window.location.search);
        const urlDate = params.get('date');
        const nowKst = new Date(Date.now() + 9 * 60 * 60 * 1000);
        const todayKst = nowKst.toISOString().slice(0, 10);
        const hasToday = response.data.find(d => d.date === todayKst);
        if (lockedDate && response.data.find(d => d.date === lockedDate)) setSelectedDate(lockedDate);
        else if (urlDate && response.data.find(d => d.date === urlDate)) setSelectedDate(urlDate);
        else if (hasToday) setSelectedDate(todayKst);
        else {
          const aprilDates = response.data.filter(d => d.date.startsWith('2026-04'));
          setSelectedDate(aprilDates.length > 0 ? aprilDates[0].date : response.data[0].date);
        }
      }
    } catch (e) { console.error(e); }
  }, [selectedDate, lockedDate]);

  const fetchTournaments = useCallback(async (date, { silent = false } = {}) => {
    if (!date) return;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`${API}/tournaments/live`, { params: { date } });
      setTournaments(response.data);
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/settings`); setSettings(data); } catch (e) { console.error('Failed to load settings:', e); }
  }, []);

  useEffect(() => { fetchDates(); fetchSettings(); }, [fetchDates, fetchSettings]);
  useEffect(() => {
    if (!selectedDate) return;
    fetchTournaments(selectedDate);
    const interval = setInterval(() => fetchTournaments(selectedDate, { silent: true }), 60000);
    return () => clearInterval(interval);
  }, [selectedDate, fetchTournaments]);

  const bgImg = settings?.hero_bg_url;
  return (
    <div className="min-h-screen bg-background-base" data-testid="poker-tour-container">
      <TopNav settings={settings} />
      <HeroBanner settings={settings} />
      <div
        className="relative"
        style={bgImg ? {
          backgroundImage: `url(${bgImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
        } : {}}
      >
        {bgImg && <div className="absolute inset-0 pointer-events-none" style={{ background: 'rgba(8,4,4,0.55)' }} />}
        <div className="relative z-10">
          <DateNav
            dates={dates}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            lockedDate={lockedDate}
            onLockToggle={handleLockToggle}
          />
          <ScheduleTable tournaments={tournaments} loading={loading} selectedDate={selectedDate || ''} />
        </div>
      </div>
      <Footer settings={settings} />
    </div>
  );
};

export default PokerTourPage;
